// Read-only preview of what a student sees on /session/:id for the session types Home.jsx
// routes to StudentPanel (CPP, SQL*, Blockly*, Auto Grouping, Helper/Helpee), composed the
// way StudentPanel composes them for a student who has just joined a fresh session.
//
// Pure function of its props: no context, no network, no storage, no timers. Designed to
// fill a 1366x720 virtual viewport (see .svp-screen in studentview-panel.scss).

import React from 'react'
import StudentPanelShell from './StudentPanelShell'
import {
  AppBarFacsimile,
  BlocklyFacsimile,
  ChatNoticeFacsimile,
  CodeBarFacsimile,
  CodeEditorFacsimile,
  CodeStuFrame,
  GroupRoomInputFacsimile,
  OutputFacsimile,
  PeerChatFacsimile,
  PeerInstructionFacsimile,
  SqlResultFacsimile,
  SqlSchemaFacsimile,
  TaskCardFacsimile,
  TestTableFacsimile,
} from './studentPanelFacsimile'
import '../../../css/studentview-panel.scss'

// Chat.jsx's text for a student with no group while session.enable_chat is off.
export const UngroupedChatNotice = () => (
  <ChatNoticeFacsimile>
    The current grouping session has concluded. You&apos;ll be assigned to a group in the
    next session. Please hang tight, and thank you for your patience!
  </ChatNoticeFacsimile>
)

// The right column a freshly joined student gets, or null when StudentPanel renders none.
// Shared with the wizard's interactive preview (live/InteractiveStudentPanelPreview), whose
// peer chat stays a facsimile too, so both always pick the same one.
// StudentPanel drops the chat column for SQL until a group is selected. Blockly shows the
// room-number entry until the student joins a group; Helper/Helpee shows Chat's "use the
// help buttons" notice until matched.
export const studentPanelChatFacsimile = (type, enableChat = false) => {
  const t = type || ''
  if (t.startsWith('Blockly')) return <GroupRoomInputFacsimile />
  if (t === 'Helper/Helpee') {
    return (
      <ChatNoticeFacsimile>
        Use the 'Ask for Help' or 'Offer Help to Others' button to help and chat with
        other students
      </ChatNoticeFacsimile>
    )
  }
  if (t.startsWith('SQL')) return null
  return enableChat ? <PeerChatFacsimile /> : <UngroupedChatNotice />
}

const editorLanguageFor = (type) => {
  if (type === 'CPP') return 'cpp'
  if (type?.startsWith('SQL')) return 'sql'
  return 'python'
}

export default function StudentPanelPreview({
  type,
  subject = '',
  task = '',
  testList = [],
  testsEnabled = true,
  template = '',
  showAppBar = true,
  // session.enable_chat. For these types it gates PEER chat, and addSession creates CPP
  // (and Auto Grouping) sessions with it off - so a newly joined, ungrouped student gets
  // Chat.jsx's "grouping session has concluded" notice, not a chat box.
  enableChat = false,
}) {
  const t = type || ''
  const isSql = t.startsWith('SQL')
  const isBlockly = t.startsWith('Blockly')
  const isHelper = t === 'Helper/Helpee'
  // addSession creates Helper/Helpee (and Audio) sessions already grouped, and
  // PeerInstruction moves to its second step for grouped sessions.
  const isGrouped = isHelper

  const left = (
    <>
      <TaskCardFacsimile subject={subject} task={task} />
      {isSql ? (
        <SqlSchemaFacsimile />
      ) : (
        testsEnabled && <TestTableFacsimile testList={testList} />
      )}
    </>
  )

  // CodeStu: CodeBar, CodeArea, then the Output panel for every non-SQL type; SQL has its
  // output in the left schema box and the result diff in CodeStu's second container.
  const code = (
    <CodeStuFrame after={isSql ? <SqlResultFacsimile /> : null}>
      <CodeBarFacsimile helpLabel={isSql || isHelper ? 'Ask for help' : null} />
      {isBlockly ? (
        <BlocklyFacsimile code={template} />
      ) : (
        <CodeEditorFacsimile code={template} language={editorLanguageFor(t)} />
      )}
      {!isSql && <OutputFacsimile />}
    </CodeStuFrame>
  )

  const chat = studentPanelChatFacsimile(t, enableChat)

  return (
    <div className="svp-screen" aria-label="Student view preview">
      {showAppBar && <AppBarFacsimile />}
      <div className="svp-home-container">
        <StudentPanelShell
          top={<PeerInstructionFacsimile activeStep={isGrouped ? 1 : 0} />}
          left={left}
          code={code}
          chat={chat}
          height="calc(85 * var(--svp-vh, 1vh))"
        />
      </div>
    </div>
  )
}
